// pages/components/DumbGroupingPanelWithApply.tsx
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from '../../../../Flux/index.js'
import type { VisualizationPipeline } from '../../SampleGrouping/RunPipeline.js'
import { runPipeline } from '../../SampleGrouping/RunPipeline.js'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'

import { selectVizSamples } from '../../../../Flux/Slice/Visualization/SelectVisualizationSamples.js'
import { resolveMetadataFields } from '../../../../Flux/Slice/Visualization/ResolveMetadataFields.js'
import { BasicModeEditor } from '../../SampleGrouping/UI/Widgets/BasicModeEditor.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { useConfirm } from '../../ConfirmProvider.js'
import { makeSelectVizBits } from './GroupingSelectors.tsx'

const SOFT_MAX_SELECTED_SAMPLES = 5_000
const HARD_MAX_SELECTED_SAMPLES = 50_000

export default function DumbGroupingPanelWithApply({ sourceId }: { sourceId: SourceId }) {
  const dispatch = useDispatch()

  const selectVizBits = useMemo(makeSelectVizBits, [])
  const { storePipeline, storeSelectedKeys, fieldsIndex, samplesLoading, ordersLoading, status, sourceExists } = useSelector(
    (s: typeof State) => selectVizBits(s, sourceId)
  )

  // local, unapplied edits
  const [pipeline, setPipeline] = useState<VisualizationPipeline>(storePipeline)
  const [selectedKeys, setSelectedKeys] = useState<string[]>(storeSelectedKeys)

  // reset local edits if store changes (e.g. when switching sources)
  useEffect(() => setPipeline(storePipeline), [storePipeline])
  useEffect(() => setSelectedKeys(storeSelectedKeys), [storeSelectedKeys])

  const samples = useSelector(selectVizSamples)
  const fields = useMemo(() => (fieldsIndex ? resolveMetadataFields(fieldsIndex) : null), [fieldsIndex])

  // preview buckets are computed locally to allow pre-selection *before* Apply
  const previewBuckets = useMemo(() => {
    if (!fields) return []
    return runPipeline(samples, pipeline, fields)
  }, [samples, pipeline, fields])

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys])

  /* compute counts for limit checking */
  const { totalSamplesAllBuckets, totalSamplesSelected } = useMemo(() => {
    let totalAll = 0
    let totalSel = 0

    for (const b of previewBuckets) {
      const size = b.samples.length
      totalAll += size
      if (selectedSet.has(b.key)) {
        totalSel += size
      }
    }

    return {
      totalSamplesAllBuckets: totalAll,
      totalSamplesSelected: totalSel
    }
  }, [previewBuckets, selectedSet])

  const dirty =
    JSON.stringify(pipeline) !== JSON.stringify(storePipeline) || JSON.stringify(selectedKeys) !== JSON.stringify(storeSelectedKeys)

  const disabled = samplesLoading || ordersLoading || !fields

  const toggleKey = (k: string) => {
    setSelectedKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))
  }

  const { confirm } = useConfirm()

  const selectAll = () => {
    const allKeys = previewBuckets.map((b) => b.key)

    const samplesIfSelectedAll = previewBuckets.slice(0, allKeys.length).reduce((sum, b) => sum + b.samples.length, 0)

    if (samplesIfSelectedAll > HARD_MAX_SELECTED_SAMPLES) {
      confirm(
        {
          title: 'Selecting all buckets',
          message:
            `You are about to select ${allKeys.length} buckets with ${samplesIfSelectedAll.toLocaleString()} samples.\n` +
            `This exceeds the hard limit for interactive visualization. Please narrow down your selection.`,
          confirmLabel: 'Ok',
          hideCancel: true
        },
        () => {}
      ).then(() => {})
      return
    }

    if (samplesIfSelectedAll > SOFT_MAX_SELECTED_SAMPLES) {
      confirm(
        {
          title: 'Selecting all buckets',
          message:
            `You are about to select ${allKeys.length} buckets ` +
            `with ${samplesIfSelectedAll.toLocaleString()} samples.\n\n` +
            `This may be slow to render and query. Do you want to continue?`,
          confirmLabel: 'Yes'
        },
        () => {
          setSelectedKeys(allKeys)
        }
      ).then(() => {})
    } else {
      setSelectedKeys(allKeys)
    }
  }

  const selectNone = () => setSelectedKeys([])

  const onApply = () => {
    // TODO: use choose here instead of alert
    if (totalSamplesSelected > HARD_MAX_SELECTED_SAMPLES) {
      confirm(
        {
          title: 'Selection error',
          message:
            `You selected ${selectedKeys.length} buckets with ${totalSamplesSelected.toLocaleString()} samples.\n` +
            `This exceeds the hard limit for interactive visualization. ` +
            `Please narrow down or try a different view/export.`,
          confirmLabel: 'Ok',
          hideCancel: true
        },
        () => {}
      ).then(() => {})
      window.alert(
        `You selected ${selectedKeys.length} buckets with ${totalSamplesSelected.toLocaleString()} samples.\n` +
          `This exceeds the hard limit for interactive visualization. ` +
          `Please narrow down or try a different view/export.`
      )
      return
    }

    if (totalSamplesSelected > SOFT_MAX_SELECTED_SAMPLES) {
      confirm(
        {
          title: 'Selection Warning',
          message:
            `You selected ${selectedKeys.length} buckets with ` +
            `${totalSamplesSelected.toLocaleString()} samples.\n\n` +
            `This may be slow to compute and could take a while.\n` +
            `Do you still want to run it?`,
          confirmLabel: 'Yes'
        },
        () => {
          dispatch(
            visualizationSlice.actions.applySourceChanges({
              sourceId,
              config: { kind: 'pipeline/v1', pipeline },
              selectedKeys
            })
          )
        }
      ).then(() => {})
    } else {
      dispatch(
        visualizationSlice.actions.applySourceChanges({
          sourceId,
          config: { kind: 'pipeline/v1', pipeline },
          selectedKeys
        })
      )
    }
  }

  const onReset = () => {
    setPipeline(storePipeline)
    setSelectedKeys(storeSelectedKeys)
  }

  /* Source not in the store yet -- the parent controls creation. This guard has to sit below every
     hook above: as an early return it skipped sixteen of them, so the first render after the source
     appeared changed the hook count and React threw "Rendered more hooks than during the previous
     render". */
  if (!sourceExists) return null

  return (
    <div className="border rounded p-2">
      {(samplesLoading || ordersLoading) && (
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs opacity-70">
            {status}
            {(samplesLoading || ordersLoading) && <span className="ml-2 loading loading-dots loading-xs" />}
          </div>
        </div>
      )}

      {!fields ? (
        <div className="text-sm opacity-70">
          Metadata fields will appear after samples & orders load and the first pipeline run initializes the fields index.
        </div>
      ) : (
        <>
          <BasicModeEditor metadataFields={fields} samples={samples} pipeline={pipeline} setPipeline={setPipeline} />

          {/* Bucket preview + selection (local) */}
          <div className="mt-4">
            <div className="flex flex-col items-center justify-between mb-2">
              <div className="font-medium">
                Buckets (preview): {previewBuckets.length}{' '}
                <span className="opacity-60">
                  selected {selectedKeys.length}/{previewBuckets.length}
                </span>
                <span className="opacity-60 ml-2 text-xs">
                  samples {totalSamplesSelected.toLocaleString()}/{totalSamplesAllBuckets.toLocaleString()}
                </span>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-xs" onClick={selectAll} disabled={disabled || previewBuckets.length === 0}>
                  Select All
                </button>
                <button className="btn btn-xs" onClick={selectNone} disabled={disabled || selectedKeys.length === 0}>
                  Select None
                </button>
              </div>
            </div>

            <div className="max-h-56 overflow-auto">
              {previewBuckets.length === 0 ? (
                <div className="p-3 text-sm opacity-70">No buckets with the current pipeline.</div>
              ) : (
                <ul className="menu w-full">
                  {previewBuckets.map((b) => (
                    <li key={b.key}>
                      <label className="cursor-pointer w-full p-0.5">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs mr-2"
                          checked={selectedSet.has(b.key)}
                          onChange={() => toggleKey(b.key)}
                        />
                        <span className="truncate text-sm" title={b.label}>
                          {b.label}
                        </span>
                        <span className="ml-auto text-xs opacity-60">{b.samples.length}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-2 flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={onApply} disabled={disabled || !dirty}>
              Apply
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onReset} disabled={!dirty}>
              Reset
            </button>
          </div>
        </>
      )}
    </div>
  )
}
